import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Activity,
  Ban,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  Eye,
  EyeOff,
  Info,
  Layers,
  Link2,
  Plus,
  Target,
  Undo2,
  Users,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { cn, formatDate } from 'src/lib/utils';
import {
  findRefSkill,
  suggestAction,
  STATUS_META,
  healableCount,
  skippedCount,
} from './skillResolutionData';
import { SuggestedActionPill } from './SuggestedActionPill';

const ACTION_ICONS = {
  ALIAS: Link2,
  NEW_SKILL: Plus,
  MAP: Check,
  BLOCK: Ban,
  IGNORE: EyeOff,
  UNIGNORE: Eye,
  UNMAP: Undo2,
};

// Keyboard shortcuts, active whenever the modal is open, no text field is
// focused, and the action dialog isn't already up. Mirrors the legend shown
// in the nav bar.
const SHORTCUT_ACTIONS = {
  a: 'ALIAS',
  n: 'NEW_SKILL',
  b: 'BLOCK',
  i: 'IGNORE',
};

// Copy for the "Affected" section's requirement list when there's no
// example row to show — three genuinely different reasons, so worth
// spelling out rather than guessing at one generic line.
function requirementsEmptyMessage(item, detailLoading) {
  if (detailLoading) return 'Loading…';
  if (item.req === 0) return 'No requirement rows carry this norm.';
  const rows = `${item.req} requirement row${item.req === 1 ? '' : 's'}`;
  if (item.reqsDetailAvailable) {
    return `Touches ${rows} — none mandatory, so no example is shown.`;
  }
  return `Touches ${rows}, but none were returned in the detail.`;
}

const NAV_ITEMS = [
  { key: 'decision', label: 'Decision' },
  { key: 'variants', label: 'Variants' },
  { key: 'nearest', label: 'Nearest' },
  { key: 'affected', label: 'Affected' },
  { key: 'history', label: 'History' },
];

const SectionCard = React.forwardRef(
  ({ icon: Icon, title, right, children }, ref) => (
    <section
      ref={ref}
      className="bg-card border border-border rounded-xl mb-3.5 scroll-mt-3"
    >
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border-light">
        <Icon
          className="w-[15px] h-[15px] text-muted-foreground"
          strokeWidth={2}
        />
        <h3 className="text-[13px] font-bold tracking-[-0.01em] text-foreground-strong">
          {title}
        </h3>
        {right && (
          <span className="ml-auto text-[11.5px] text-muted-foreground">
            {right}
          </span>
        )}
      </div>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  )
);
SectionCard.displayName = 'SectionCard';
SectionCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  right: PropTypes.node,
  children: PropTypes.node.isRequired,
};
SectionCard.defaultProps = { right: null };

function RecommendationBanner({ item }) {
  const sg = suggestAction(item);
  if (item.status === 'blocked') {
    return (
      <Banner tone="danger" icon={Ban}>
        <strong className="font-semibold">Blocked.</strong> Future parses scrub
        this term. The {healableCount(item)} existing rows are untouched and
        stay visible here.
      </Banner>
    );
  }
  if (item.status === 'resolved') {
    return (
      <Banner tone="success" icon={CheckCircle2}>
        <strong className="font-semibold">Resolved.</strong> No unresolved rows
        carry this norm. If a new parse writes one, it reopens here on its own.
      </Banner>
    );
  }
  if (item.status === 'ignored') {
    return (
      <Banner tone="warning" icon={EyeOff}>
        <strong className="font-semibold">Ignored.</strong> Hidden from the
        default queue but still counting occurrences. Unignore to bring it back.
      </Banner>
    );
  }
  if (item.exact) {
    return (
      <Banner tone="success" icon={CheckCircle2}>
        <strong className="font-semibold">
          The taxonomy already has this skill
        </strong>{' '}
        — {item.exact.name} (id {item.exact.id}), matched on {item.exact.via}.{' '}
        <span className="text-muted-foreground">
          Only history needs linking, so Map is the whole job: no alias, no new
          skill.
        </span>
      </Banner>
    );
  }
  if (sg === 'ALIAS') {
    const n = item.nearest[0];
    return (
      <Banner tone="info" icon={Link2}>
        <strong className="font-semibold">
          Reads like a surface form of {n[1]}
        </strong>{' '}
        (avg {n[3].toFixed(3)}, above the 0.70 triage line).{' '}
        <span className="text-muted-foreground">
          Check the variants below before you commit — the score is a hint, not
          a verdict.
        </span>
      </Banner>
    );
  }
  if (sg === 'NEW_SKILL') {
    const n = item.nearest[0];
    return (
      <Banner tone="warning" icon={Plus}>
        <strong className="font-semibold">Nothing close in the taxonomy</strong>{' '}
        — best neighbour is {n[1]} at {n[3].toFixed(3)}.{' '}
        <span className="text-muted-foreground">
          If it is a real skill, create it. If it is a trait or an
          employer-internal tool, block it instead.
        </span>
      </Banner>
    );
  }
  if (sg === 'IGNORE') {
    return (
      <Banner tone="warning" icon={EyeOff}>
        <strong className="font-semibold">Too rare to spend taxonomy on</strong>{' '}
        — under three occurrences.{' '}
        <span className="text-muted-foreground">
          Ignore keeps it out of your way without deciding anything permanent.
        </span>
      </Banner>
    );
  }
  return (
    <Banner tone="info" icon={Info}>
      <strong className="font-semibold">No nearest match recorded.</strong>{' '}
      There is no parse-time score for this norm yet, so there is nothing to
      judge against — decide from the raw variants below.
    </Banner>
  );
}
RecommendationBanner.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
};

const BANNER_TONES = {
  danger: 'bg-ds-danger-subtle text-ds-danger',
  success: 'bg-ds-success-subtle text-ds-success',
  warning: 'bg-ds-warning-subtle text-ds-warning',
  info: 'bg-brand-bg-subtle text-primary',
};

function Banner({ tone, icon: Icon, children }) {
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 px-3.5 py-3 rounded-lg text-[12.5px] leading-[1.5]',
        BANNER_TONES[tone]
      )}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} />
      <span className="text-foreground-strong">{children}</span>
    </div>
  );
}
Banner.propTypes = {
  tone: PropTypes.oneOf(['danger', 'success', 'warning', 'info']).isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const RecordDrawer = ({
  open,
  onClose,
  item,
  detailLoading,
  position,
  total,
  refSkills,
  autoAdvance,
  onAutoAdvanceChange,
  actionOpen,
  onPrev,
  onNext,
  onAction,
}) => {
  const bodyRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState('decision');

  // Jump back to the top and re-sync the active nav item every time the
  // reviewed norm changes (prev/next, or reopening on a different row).
  useEffect(() => {
    if (!open) return;
    bodyRef.current?.scrollTo({ top: 0 });
    setActiveSection('decision');
  }, [open, item?.norm]);

  useEffect(() => {
    if (!open || actionOpen) return undefined;
    const handler = (e) => {
      const tag = (e.target.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (item?.status === 'open') {
        const action = SHORTCUT_ACTIONS[e.key.toLowerCase()];
        if (action) {
          e.preventDefault();
          onAction(action);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, actionOpen, item?.status, onPrev, onNext, onAction]);

  if (!item) return null;
  const sg = suggestAction(item);
  const wouldHeal = healableCount(item) - skippedCount(item);
  const skipped = skippedCount(item);

  const handleBodyScroll = () => {
    const body = bodyRef.current;
    if (!body) return;
    const top = body.scrollTop + 90;
    let current = NAV_ITEMS[0].key;
    NAV_ITEMS.forEach(({ key }) => {
      const el = sectionRefs.current[key];
      if (el && el.offsetTop <= top) current = key;
    });
    setActiveSection(current);
  };

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    const body = bodyRef.current;
    if (el && body) {
      body.scrollTo({ top: el.offsetTop - 12, behavior: 'smooth' });
      setActiveSection(key);
    }
  };

  const footerActions = () => {
    const btn = (action, label, variant) => {
      const Icon = ACTION_ICONS[action];
      return (
        <Button
          key={action}
          variant={variant}
          size="sm"
          onClick={() => onAction(action)}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          {label}
        </Button>
      );
    };
    if (item.status === 'ignored') {
      return (
        <>
          {btn('UNIGNORE', 'Return to queue', 'default')}
          {btn('BLOCK', 'Block term', 'destructive')}
        </>
      );
    }
    if (item.status === 'blocked') {
      return (
        <>
          <span className="text-[11.5px] text-muted-foreground mr-2">
            Blocked terms are managed from the blocklist admin.
          </span>
          {btn('IGNORE', 'Hide from queue', 'outline')}
        </>
      );
    }
    if (item.status === 'resolved') {
      return (
        <>
          {btn('UNMAP', 'Reverse this heal', 'destructive')}
          {btn('IGNORE', 'Hide from queue', 'outline')}
        </>
      );
    }
    const order = ['IGNORE', 'BLOCK', 'MAP', 'ALIAS', 'NEW_SKILL'];
    const labels = {
      IGNORE: 'Ignore',
      BLOCK: 'Block',
      MAP: 'Map only',
      ALIAS: 'Add alias',
      NEW_SKILL: 'New skill',
    };
    return order.map((a) => {
      // Only the suggested action gets visual weight — everything else
      // stays on the same subtle `outline` treatment, Block included, so a
      // non-suggested Block doesn't compete with the actual recommendation.
      let variant = 'outline';
      if (a === sg) variant = a === 'BLOCK' ? 'destructive' : 'default';
      return btn(a, labels[a], variant);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[1180px] w-[94vw] h-[88vh] p-0 flex flex-col gap-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 px-6 py-4 border-b border-border shrink-0">
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-mono text-[18px] font-bold tracking-[-0.01em] text-foreground-strong break-words">
              {item.norm}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Review and resolve the unmapped skill &quot;{item.norm}&quot;.
            </DialogDescription>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1.5 text-[13px] text-muted-foreground">
              <span>
                {item.cand + item.req} unresolved rows — {item.cand} candidate ·{' '}
                {item.req} requirement
              </span>
              <span className="text-muted-foreground/50">·</span>
              <span>Last seen {formatDate(item.lastSeen)}</span>
              <SuggestedActionPill action={sg} showPrefix />
              {item.mandatory && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-[11.5px] font-semibold bg-ds-warning-subtle text-ds-warning">
                  Touches a mandatory requirement
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={onPrev}
              disabled={position <= 1}
              aria-label="Previous norm"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-mono text-[13px] font-semibold text-muted-foreground w-[68px] text-center">
              {position} of {total}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={onNext}
              disabled={position >= total}
              aria-label="Next norm"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <DialogClose asChild>
              <Button variant="outline" size="sm" className="ml-2 gap-1.5 h-9">
                <X className="w-3.5 h-3.5" strokeWidth={2} />
                Close
                <span className="ml-1 px-1.5 py-px rounded border border-border-light bg-muted text-[10px] font-semibold text-muted-foreground">
                  Esc
                </span>
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Section nav */}
        <div className="flex items-center gap-1 px-6 border-b border-border shrink-0">
          {NAV_ITEMS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => scrollToSection(key)}
              className={cn(
                'px-3.5 py-2.5 text-[13px] border-b-2 -mb-px whitespace-nowrap transition-colors',
                activeSection === key
                  ? 'font-semibold border-primary text-primary'
                  : 'font-medium border-transparent text-muted-foreground hover:text-foreground-strong'
              )}
            >
              {label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground whitespace-nowrap pl-4">
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              ←
            </kbd>
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              →
            </kbd>
            move ·{' '}
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              A
            </kbd>{' '}
            alias ·{' '}
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              N
            </kbd>{' '}
            new skill ·{' '}
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              B
            </kbd>{' '}
            block ·{' '}
            <kbd className="px-1.5 py-px rounded border border-border-light bg-muted font-semibold">
              I
            </kbd>{' '}
            ignore
          </div>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          onScroll={handleBodyScroll}
          className="flex-1 overflow-y-auto px-6 py-4 bg-muted/40"
        >
          <div className="grid grid-cols-[1.35fr_1fr] gap-4 items-start">
            <div className="min-w-0">
              <SectionCard
                ref={(el) => {
                  sectionRefs.current.decision = el;
                }}
                icon={Target}
                title="Decision"
                right="suggested_action is a hint"
              >
                <RecommendationBanner item={item} />
                <div className="grid grid-cols-2 gap-2.5 mt-3.5">
                  <div className="border border-border-light rounded-lg px-3 py-2.5 bg-muted/60">
                    <div className="font-mono text-[19px] font-bold text-foreground-strong leading-[1.1]">
                      {wouldHeal}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      rows would heal
                    </div>
                  </div>
                  <div className="border border-border-light rounded-lg px-3 py-2.5 bg-muted/60">
                    <div
                      className={cn(
                        'font-mono text-[19px] font-bold leading-[1.1]',
                        skipped ? 'text-ds-warning' : 'text-foreground-strong'
                      )}
                    >
                      {skipped}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      skipped — row already has that skill
                    </div>
                  </div>
                </div>
                <p className="text-[11.5px] text-muted-foreground mt-3 leading-[1.5]">
                  Computed with the same guard the heal uses (
                  <code className="font-mono">NOT EXISTS</code> +{' '}
                  <code className="font-mono">MIN(id)</code>), so this is what
                  will happen, not an estimate. Skipped rows stay unresolved and
                  stay in the queue.
                </p>
              </SectionCard>

              <SectionCard
                ref={(el) => {
                  sectionRefs.current.variants = el;
                }}
                icon={Layers}
                title="Raw variants"
                right={`${item.variants.length} surface forms`}
              >
                <div className="flex flex-wrap gap-2">
                  {item.variants.map((v) => (
                    <span
                      key={v[0]}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border-light text-[12px] text-foreground-strong"
                    >
                      {v[0]}
                      {v[1] != null && (
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground">
                          ×{v[1]}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
                <p className="text-[11.5px] text-muted-foreground mt-2.5">
                  The most frequent form is what an alias or a new skill name
                  defaults to.
                </p>
              </SectionCard>

              <SectionCard
                ref={(el) => {
                  sectionRefs.current.nearest = el;
                }}
                icon={Activity}
                title="Nearest reference skills"
                right="parse-time, never overwritten"
              >
                {item.nearest.length ? (
                  <div className="divide-y divide-border-light">
                    {item.nearest.map((n) => (
                      <div
                        key={n[0]}
                        className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-foreground-strong">
                            {n[1]}
                          </div>
                          <div className="font-mono text-[11px] text-muted-foreground">
                            id {n[0]} · {n[2]} rows · avg {n[3].toFixed(3)}
                          </div>
                        </div>
                        <div className="w-[110px] h-1.5 rounded-full bg-muted overflow-hidden shrink-0">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              n[3] >= 0.7 ? 'bg-ds-success' : 'bg-ds-warning'
                            )}
                            style={{ width: `${Math.round(n[3] * 100)}%` }}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onAction('ALIAS', n[0])}
                        >
                          Alias to this
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-muted-foreground">
                    {detailLoading
                      ? 'Loading…'
                      : 'No nearest match was recorded for these rows.'}
                  </p>
                )}
              </SectionCard>

              <SectionCard
                ref={(el) => {
                  sectionRefs.current.affected = el;
                }}
                icon={Users}
                title="What this touches"
              >
                <dl className="grid grid-cols-2 gap-y-1.5 text-[12.5px] mb-3">
                  <dt className="text-muted-foreground">
                    Candidate skill rows
                  </dt>
                  <dd className="text-foreground-strong font-semibold text-right">
                    {item.cand}
                  </dd>
                  <dt className="text-muted-foreground">
                    Requirement skill rows
                  </dt>
                  <dd className="text-foreground-strong font-semibold text-right">
                    {item.req}
                  </dd>
                </dl>
                {item.reqs.length ? (
                  <div className="space-y-1.5 mb-3">
                    {item.reqs.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center gap-2.5 px-2.5 py-2 border border-border-light rounded-lg text-[12.5px]"
                      >
                        <span className="font-mono text-[11px] text-muted-foreground">
                          REQ-{r.id}
                        </span>
                        <span className="flex-1 text-foreground-strong font-medium truncate">
                          {r.title}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 inline-flex items-center gap-1.5 rounded-sm border border-border py-0.5 pl-2.5 pr-1 text-[11px] font-semibold',
                            r.mandatory
                              ? 'bg-brand-bg-subtle text-primary'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {r.mandatory ? 'Must-have' : 'Nice-to-have'}
                          <span
                            className={cn(
                              'rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-[0.03em]',
                              r.mandatory
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-border text-muted-foreground'
                            )}
                          >
                            {r.mandatory ? 'REQ' : 'OPT'}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-muted-foreground mb-3">
                    {requirementsEmptyMessage(item, detailLoading)}
                  </p>
                )}
                <div className="text-[11px] font-semibold text-foreground-strong mb-1">
                  Sample candidate rows
                </div>
                {item.sampleIds.length ? (
                  <div className="font-mono text-[11px] text-muted-foreground leading-[1.9] break-all">
                    {item.sampleIds.map((id) => (
                      <div key={id}>{id}</div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-muted-foreground">
                    {detailLoading ? 'Loading…' : 'No sample rows returned.'}
                  </p>
                )}
              </SectionCard>
            </div>

            <div className="min-w-0">
              <SectionCard icon={Database} title="Row facts">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-[12.5px]">
                  <dt className="text-muted-foreground">name_norm</dt>
                  <dd className="font-mono text-[12px] text-foreground-strong">
                    {item.norm}
                  </dd>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="text-foreground-strong font-medium">
                    {STATUS_META[item.status].label}
                  </dd>
                  <dt className="text-muted-foreground">match_method</dt>
                  <dd className="text-foreground-strong">
                    {item.legacy ? 'NULL — legacy, read as NONE' : 'NONE'}
                  </dd>
                  <dt className="text-muted-foreground">Exact hit</dt>
                  <dd className="text-foreground-strong">
                    {item.exact
                      ? `${item.exact.name} (id ${item.exact.id})`
                      : '—'}
                  </dd>
                  <dt className="text-muted-foreground">Last seen</dt>
                  <dd className="text-foreground-strong">
                    {formatDate(item.lastSeen)}
                  </dd>
                </dl>
              </SectionCard>

              <SectionCard
                ref={(el) => {
                  sectionRefs.current.history = el;
                }}
                icon={Clock}
                title="Action history"
              >
                {item.history.length ? (
                  <div className="space-y-0">
                    {item.history
                      .slice()
                      .reverse()
                      .map((h) => {
                        const HIcon = ACTION_ICONS[h.action] || Activity;
                        const healed = h.result?.healed
                          ? `${h.result.healed.candidate} candidate · ${h.result.healed.requirement} requirement rows healed`
                          : '';
                        const linkedName = h.payload?.ref_skill_id
                          ? findRefSkill(refSkills, h.payload.ref_skill_id)
                              ?.name
                          : null;
                        return (
                          <div
                            key={`${h.action}-${h.at}`}
                            className="flex gap-3 py-2.5 border-b border-border-light last:border-b-0"
                          >
                            <span className="w-7 h-7 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                              <HIcon className="w-3.5 h-3.5" strokeWidth={2} />
                            </span>
                            <div className="min-w-0">
                              <div className="text-[12.5px] font-semibold text-foreground-strong">
                                {h.action}
                                {linkedName ? ` — ${linkedName}` : ''}
                              </div>
                              <div className="text-[11.5px] text-muted-foreground mt-0.5">
                                <span className="font-mono">{h.actor}</span> ·{' '}
                                {formatDate(h.at)}
                                {healed ? ` · ${healed}` : ''}
                                {h.payload?.reason
                                  ? ` · "${h.payload.reason}"`
                                  : ''}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-muted-foreground">
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {detailLoading && item.hasHistory
                      ? 'Loading…'
                      : item.hasHistory
                        ? "This norm has prior actions, but the detail isn't available from this list view yet."
                        : 'No actions yet. Whatever you do next lands here with your name on it.'}
                  </p>
                )}
              </SectionCard>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-3.5 border-t border-border bg-card shrink-0 flex-wrap shadow-[0_-2px_10px_rgba(16,32,51,0.05)]">
          <span className="text-[11.5px] text-muted-foreground mr-auto">
            Acting as{' '}
            <span className="font-mono font-semibold text-foreground-strong">
              you
            </span>{' '}
            · every action is written to the audit trail
          </span>
          <label
            htmlFor="rm-auto-advance"
            className="flex items-center gap-2 text-[12px] text-muted-foreground mr-2 whitespace-nowrap"
          >
            <input
              id="rm-auto-advance"
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => onAutoAdvanceChange(e.target.checked)}
              className="accent-primary"
            />
            Move to the next norm after a decision
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {footerActions()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

RecordDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object,
  detailLoading: PropTypes.bool,
  position: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  refSkills: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ).isRequired,
  autoAdvance: PropTypes.bool.isRequired,
  onAutoAdvanceChange: PropTypes.func.isRequired,
  actionOpen: PropTypes.bool,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};
RecordDrawer.defaultProps = {
  item: null,
  detailLoading: false,
  actionOpen: false,
};

export default RecordDrawer;
